/**
 * Horizon - Produtos Carrossel
 * - Setas: scrollBy do track
 * - Comprar: POST /cart/add.js
 * - Refresh do cart drawer + bubble via Section Rendering API
 * - Abre o cart-drawer atualizado
 *
 * Fixes:
 * - Força alinhamento do heading do drawer (cart (1)) no início após re-render
 */

class HzProdutosCarrossel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-hz-track]');
    this.btnPrev = this.querySelector('[data-hz-prev]');
    this.btnNext = this.querySelector('[data-hz-next]');

    this.onPrev = () => this.scrollByCard(-1);
    this.onNext = () => this.scrollByCard(1);

    this.btnPrev?.addEventListener('click', this.onPrev);
    this.btnNext?.addEventListener('click', this.onNext);

    this.addEventListener(
      'click',
      (e) => {
        const btn = e.target?.closest?.('[data-hz-add]');
        if (!btn) return;
        e.preventDefault();
        this.handleAdd(btn);
      },
      { passive: false }
    );
  }

  disconnectedCallback() {
    this.btnPrev?.removeEventListener('click', this.onPrev);
    this.btnNext?.removeEventListener('click', this.onNext);
  }

  scrollByCard(direction) {
    if (!this.track) return;

    const firstCard = this.track.querySelector('[data-hz-card]');
    const gap = this.getGridGap(this.track);
    const cardWidth = firstCard ? firstCard.getBoundingClientRect().width : 320;

    const delta = (cardWidth + gap) * direction;
    this.track.scrollBy({ left: delta, behavior: 'smooth' });
  }

  getGridGap(el) {
    try {
      const styles = getComputedStyle(el);
      const gap = parseFloat(styles.columnGap || styles.gap || '0');
      return Number.isFinite(gap) ? gap : 0;
    } catch {
      return 0;
    }
  }

  async handleAdd(buttonEl) {
    const variantId = buttonEl.getAttribute('data-variant-id');
    if (!variantId) return;

    const originalText = buttonEl.textContent;
    buttonEl.disabled = true;
    buttonEl.textContent = 'ADICIONANDO...';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1,
        }),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || 'Falha ao adicionar no carrinho');
      }

      const data = await res.json();

      // eventos genéricos (ajuda integrações do tema)
      const payload = { detail: { item: data }, bubbles: true };
      document.dispatchEvent(new CustomEvent('cart:add', payload));
      document.dispatchEvent(new CustomEvent('cart:refresh', payload));
      document.dispatchEvent(new CustomEvent('cart:change', payload));

      // Atualiza drawer + bubble antes de abrir
      await this.refreshDrawerAndBubble();

      // Abre drawer atualizado
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer?.open) drawer.open();
      else document.querySelector('[data-testid="cart-drawer-trigger"]')?.click?.();

      buttonEl.textContent = 'ADICIONADO';
      window.setTimeout(() => {
        buttonEl.textContent = originalText;
        buttonEl.disabled = false;
      }, 900);
    } catch (err) {
      console.error('[hz-produtos] add-to-cart error:', err);
      buttonEl.textContent = 'ERRO';
      window.setTimeout(() => {
        buttonEl.textContent = originalText;
        buttonEl.disabled = false;
      }, 1200);
    }
  }

  async refreshDrawerAndBubble() {
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;

    // No Horizon, cart-items-component tem data-section-id (como no seu snippet)
    const cartItems = drawer.querySelector('cart-items-component[data-section-id]');
    const sectionId = cartItems?.getAttribute('data-section-id');
    if (!sectionId) return;

    // Section Rendering API -> retorna JSON { [sectionId]: "<html...>" }
    const url = new URL(window.location.href);
    url.searchParams.set('sections', sectionId);

    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });

    if (!res.ok) return;

    const json = await res.json();
    const html = json?.[sectionId];
    if (!html) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');

    // 1) Atualiza o miolo do drawer
    const newDrawer = doc.querySelector('cart-drawer-component');
    const newInner = newDrawer?.querySelector('.cart-drawer__inner');
    const currentInner = drawer.querySelector('.cart-drawer__inner');

    if (newInner && currentInner) {
      currentInner.innerHTML = newInner.innerHTML;
    }

    // 2) Atualiza o bubble do ícone do carrinho
    const currentCartIcon = document.querySelector('cart-icon.header-actions__cart-icon');
    const newCartIcon = doc.querySelector('cart-icon.header-actions__cart-icon');

    if (currentCartIcon && newCartIcon) {
      const currentBubble = currentCartIcon.querySelector('.cart-bubble');
      const newBubble = newCartIcon.querySelector('.cart-bubble');

      if (currentBubble && newBubble) {
        currentBubble.replaceWith(newBubble);
      } else {
        currentCartIcon.innerHTML = newCartIcon.innerHTML;
      }
    }

    // FIX: garante alinhamento do título (ex: "CART (1)") no início
    // (às vezes, após innerHTML swap, um estado/classe do tema deixa ir pro fim)
    const header = drawer.querySelector('.cart-drawer__header');
    const heading = drawer.querySelector('.cart-drawer__heading');

    if (header) {
      header.style.justifyContent = 'space-between';
      header.style.textAlign = 'left';
    }

    if (heading) {
      heading.style.justifyContent = 'flex-start';
      heading.style.textAlign = 'left';
      heading.style.marginRight = 'auto';
    }
  }
}

if (!customElements.get('hz-produtos-carrossel')) {
  customElements.define('hz-produtos-carrossel', HzProdutosCarrossel);
}
